<template>
  <div class="md-aff">
    <div v-if="!rows.length" class="md-aff__empty">{{ $t('mobileDash.noData') }}</div>
    <template v-else>
      <button
          v-for="(row, i) in visibleRows" :key="row.key || i"
          type="button"
          class="md-aff__card"
          :class="{ 'md-aff__card--dup': row.key === 'duplicated' }"
          @click="$emit('select', row)"
      >
        <span class="md-aff__top">
          <span v-if="row.key === 'duplicated'" class="md-aff__pos md-aff__pos--dup">
            <i class="fa-solid fa-copy"></i>
          </span>
          <span v-else class="md-aff__pos" :class="{ 'md-aff__pos--top': i === 0 }">{{ i + 1 }}</span>
          <span class="md-aff__name" :class="{ 'md-aff__name--dup': row.key === 'duplicated' }">{{ row.label }}</span>
          <span class="md-aff__net" :class="(row.netTotal || 0) >= 0 ? 'is-pos' : 'is-neg'">
            {{ fullEuro(row.netTotal || 0) }}
            <span class="md-aff__net-label">{{ $t('mobileDash.affCols.net') }}</span>
          </span>
        </span>
        <span class="md-aff__grid">
          <span class="md-aff__cell">
            <span class="md-aff__cell-label">{{ $t('mobileDash.affCols.leads') }}</span>
            <span class="md-aff__cell-value md-aff__cell-value--leads">{{ wholeNumber(row.leads || 0) }}</span>
          </span>
          <span class="md-aff__cell">
            <span class="md-aff__cell-label">{{ $t('mobileDash.affCols.ftd') }}</span>
            <span class="md-aff__cell-value md-aff__cell-value--ftd">{{ wholeNumber(row.ftds || 0) }}</span>
          </span>
          <span class="md-aff__cell">
            <span class="md-aff__cell-label">{{ $t('mobileDash.affCols.conv') }}</span>
            <span class="md-aff__cell-value md-aff__cell-value--conv">{{ pct(row.conversionRate) }}</span>
          </span>
          <!-- Compact money in the mini table keeps it ONE row; the exact
               amount is the full-price Net above and in the drill-down -->
          <span class="md-aff__cell">
            <span class="md-aff__cell-label">{{ $t('mobileDash.affCols.deposits') }}</span>
            <span class="md-aff__cell-value md-aff__cell-value--in">{{ compactEuro(row.netDeposits || 0) }}</span>
          </span>
          <span class="md-aff__cell">
            <span class="md-aff__cell-label">{{ $t('mobileDash.affCols.withdrawals') }}</span>
            <span class="md-aff__cell-value md-aff__cell-value--out">{{ compactEuro(Math.abs(row.withdrawals || 0)) }}</span>
          </span>
        </span>
      </button>
      <button v-if="rows.length > limit" type="button" class="md-aff__more" @click="expanded = !expanded">
        {{ expanded ? $t('mobileDash.showLess') : $t('mobileDash.viewAll', { count: rows.length }) }}
      </button>
    </template>
  </div>
</template>

<script>
import { fullEuro, compactEuro, wholeNumber, pct } from '@/components/mobile/mdFormat';

export default {
  name: 'MdAffiliateList',
  props: {
    // Raw MobileDashboardRankRow objects (affiliates grouping)
    rows: { type: Array, default: () => [] },
    limit: { type: Number, default: 5 },
  },
  data: () => ({ expanded: false }),
  computed: {
    visibleRows() {
      return this.expanded ? this.rows : this.rows.slice(0, this.limit);
    },
  },
  watch: {
    rows() {
      this.expanded = false;
    },
  },
  methods: { fullEuro, compactEuro, wholeNumber, pct },
};
</script>

<style scoped>
.md-aff {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.md-aff__empty {
  padding: 1.1rem;
  font-size: 12.5px;
  text-align: center;
  color: var(--md-muted, #8a919c);
  background: var(--md-card-bg, #fff);
  border: 1px solid var(--md-card-border, rgba(34, 41, 47, 0.08));
  border-radius: 10px;
}
.md-aff__card {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.65rem 0.8rem 0.7rem;
  background: var(--md-card-bg, #fff);
  border: 1px solid var(--md-card-border, rgba(34, 41, 47, 0.08));
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(34, 41, 47, 0.06), 0 5px 12px rgba(34, 41, 47, 0.1);
  transition: transform 0.08s ease, box-shadow 0.15s ease;
}
.md-aff__card:active {
  transform: scale(0.985);
  box-shadow: 0 1px 2px rgba(34, 41, 47, 0.08);
}
.dark-layout .md-aff__card {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.28), 0 7px 16px rgba(0, 0, 0, 0.22);
}
/* The Duplicated bucket (no-affiliate clients) stands apart: amber accent */
.md-aff__card--dup {
  background: linear-gradient(135deg, rgba(255, 190, 61, 0.12) 0%, rgba(255, 159, 67, 0.05) 100%);
  border-color: rgba(255, 159, 67, 0.45);
  border-left: 3px solid #ff9f43;
}
.md-aff__pos--dup {
  color: #b07d1a;
  background: rgba(255, 190, 61, 0.22);
  font-size: 10px;
}
.md-aff__name--dup {
  color: #b07d1a;
}
.dark-layout .md-aff__card--dup {
  background: linear-gradient(135deg, rgba(255, 190, 61, 0.16) 0%, rgba(255, 159, 67, 0.07) 100%);
}
.dark-layout .md-aff__name--dup,
.dark-layout .md-aff__pos--dup {
  color: #ffb976;
}
.md-aff__top {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.55rem;
}
.md-aff__pos {
  flex: 0 0 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  color: var(--md-muted, #8a919c);
  background: rgba(138, 145, 156, 0.12);
}
.md-aff__pos--top {
  color: #b07d1a;
  background: rgba(255, 190, 61, 0.18);
}
.md-aff__name {
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  font-weight: 700;
  color: var(--md-strong, #24292f);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.md-aff__net {
  font-size: 14px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.1;
}
.md-aff__net.is-pos { color: #1f9d61; }
.md-aff__net.is-neg { color: #d64545; }
.md-aff__net-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--md-muted, #8a919c);
}
/* The scoreboard: same 5 labeled columns in ONE row on every card */
.md-aff__grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 4px;
  padding-top: 0.5rem;
  border-top: 1px dashed var(--md-card-border, rgba(34, 41, 47, 0.1));
}
.md-aff__cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  min-width: 0;
}
.md-aff__cell-label {
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--md-muted, #8a919c);
  white-space: nowrap;
}
.md-aff__cell-value {
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.md-aff__cell-value--ftd { color: #6a5fe0; }
.md-aff__cell-value--leads { color: #0f89b8; }
.md-aff__cell-value--conv { color: #c47b28; }
.md-aff__cell-value--in { color: #1f9d61; }
.md-aff__cell-value--out { color: #d64545; }
.md-aff__more {
  display: block;
  width: 100%;
  padding: 0.55rem;
  font-size: 12px;
  font-weight: 600;
  color: var(--primary, #7367f0);
  background: transparent;
  border: 1px dashed rgba(138, 145, 156, 0.35);
  border-radius: 9px;
}

.dark-layout .md-aff__card,
.dark-layout .md-aff__empty {
  --md-card-bg: #283046;
  --md-card-border: #3b4253;
  --md-strong: #d0d2d6;
}
.dark-layout .md-aff__cell-value--ftd { color: #9d94f5; }
.dark-layout .md-aff__cell-value--leads { color: #4fc3e8; }
.dark-layout .md-aff__cell-value--conv { color: #ffb976; }
.dark-layout .md-aff__cell-value--in { color: #48d989; }
.dark-layout .md-aff__cell-value--out { color: #f08182; }
</style>
